import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  UserPlus, PhoneOff, Phone, PhoneCall, PhoneIncoming, PhoneOutgoing, FileText,
  Calendar, CheckCircle2, XCircle, StickyNote, Sparkles, Send, Loader2, Clock,
  CalendarClock, Store, MessageSquare, ArrowDownLeft, ArrowUpRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { leadsAPI } from '@/lib/api';

const EVENT_META = {
  created:              { icon: Sparkles,     tone: 'bg-slate-100 text-slate-700',     label: 'Lead created' },
  assigned:             { icon: UserPlus,     tone: 'bg-blue-100 text-blue-700',       label: 'Assigned' },
  status_attempting_contact: { icon: PhoneOff, tone: 'bg-amber-100 text-amber-700',    label: 'Attempting contact' },
  status_contacted:     { icon: Phone,        tone: 'bg-blue-100 text-blue-700',       label: 'Contacted' },
  status_quoted:        { icon: FileText,     tone: 'bg-indigo-100 text-indigo-700',   label: 'Quoted' },
  status_appointment:   { icon: Calendar,     tone: 'bg-purple-100 text-purple-700',   label: 'Appointment set' },
  status_closed_won:    { icon: CheckCircle2, tone: 'bg-emerald-100 text-emerald-700', label: 'Closed Won' },
  status_not_interested:{ icon: XCircle,      tone: 'bg-rose-100 text-rose-700',       label: 'Not Interested' },
  note:                 { icon: StickyNote,   tone: 'bg-yellow-100 text-yellow-700',   label: 'Note' },
  call_disposition:     { icon: PhoneCall,    tone: 'bg-emerald-100 text-emerald-700', label: 'Call logged' },
  linked_to_event:      { icon: CalendarClock, tone: 'bg-purple-100 text-purple-700',  label: 'Linked to event' },
  linked_to_retail_assignment: { icon: Store, tone: 'bg-emerald-100 text-emerald-700', label: 'Linked to retail assignment' },
};

const DISPOSITION_LABEL = {
  answered: 'Answered', voicemail: 'Left voicemail', no_answer: 'No answer',
  forwarded: 'Forwarded', in_progress: 'In progress', unknown: 'Call',
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function fmtDuration(secs) {
  const s = Number(secs) || 0;
  if (!s) return null;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

// Normalizes each unified-feed item into a presentation descriptor.
function describe(item) {
  if (item.kind === 'event') {
    const meta = EVENT_META[item.event_type] || { icon: StickyNote, tone: 'bg-slate-100 text-slate-700', label: item.event_type };
    const detail = item.event_type === 'call_disposition' && item.meta?.disposition
      ? (DISPOSITION_LABEL[item.meta.disposition] || String(item.meta.disposition).replace(/_/g, ' '))
      : null;
    return { icon: meta.icon, tone: meta.tone, title: meta.label, detail, body: item.note, actor: item.actor_name };
  }
  if (item.kind === 'sms') {
    const out = item.direction === 'outbound';
    return {
      icon: out ? ArrowUpRight : ArrowDownLeft,
      tone: out ? 'bg-sky-100 text-sky-700' : 'bg-purple-100 text-purple-700',
      title: out ? 'SMS sent' : 'SMS received',
      detail: item.status ? String(item.status) : null,
      body: item.body,
      actor: item.actor_name,
    };
  }
  if (item.kind === 'call') {
    const out = item.direction === 'outbound';
    const dur = fmtDuration(item.duration);
    const disp = DISPOSITION_LABEL[item.disposition] || 'Call';
    return {
      icon: out ? PhoneOutgoing : PhoneIncoming,
      tone: item.disposition === 'no_answer' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700',
      title: out ? 'Outbound call' : 'Inbound call',
      detail: [disp, dur].filter(Boolean).join(' · '),
      body: null,
    };
  }
  if (item.kind === 'appointment') {
    return {
      icon: Calendar,
      tone: 'bg-purple-100 text-purple-700',
      title: 'Appointment',
      detail: [item.status, item.mode, item.duration_minutes ? `${item.duration_minutes} min` : null].filter(Boolean).join(' · '),
      body: null,
    };
  }
  return { icon: StickyNote, tone: 'bg-slate-100 text-slate-700', title: item.kind, body: null };
}

// Unified lead activity: merges CRM events, SMS, calls and appointments
// into one chronological feed, with a quick-note composer on top.
export default function LeadUnifiedTimeline({ leadId, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [note, setNote] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await leadsAPI.activity(leadId);
      setItems(res.data.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    leadsAPI.noteTemplates().then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const groups = {};
    templates.forEach((t) => {
      groups[t.category] = groups[t.category] || [];
      groups[t.category].push(t);
    });
    return groups;
  }, [templates]);

  const applyTemplate = (tpl) => {
    setNote((prev) => (prev ? `${prev.trim()}\n${tpl.text}` : tpl.text));
    setSelectedTemplate(tpl.id);
  };

  const submitNote = async () => {
    if (!note.trim()) { toast.error('Write a note first'); return; }
    setSaving(true);
    try {
      await leadsAPI.addNote(leadId, note.trim(), selectedTemplate);
      toast.success('Note added');
      setNote('');
      setSelectedTemplate(null);
      load();
      if (onChange) onChange();
    } catch { toast.error('Failed to add note'); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="lead-unified-timeline">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock size={14} className="text-slate-500" /> Activity
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick note composer */}
        <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-slate-600">Quick note</p>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="note-templates-btn">
                  <Sparkles size={12} className="mr-1" /> Templates
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Pick a template</p>
                {Object.entries(grouped).map(([cat, list]) => (
                  <div key={cat} className="mb-2 last:mb-0">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mt-1">{cat}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {list.map((t) => (
                        <Button
                          key={t.id}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => applyTemplate(t)}
                          data-testid={`note-tpl-${t.id}`}
                          title={t.text}
                        >
                          {t.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
          </div>
          <Textarea
            rows={2}
            value={note}
            onChange={(e) => { setNote(e.target.value); setSelectedTemplate(null); }}
            placeholder="Log a note about this lead — or pick a template →"
            className="bg-white text-sm"
            data-testid="quick-note-input"
          />
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={submitNote} disabled={saving || !note.trim()} data-testid="quick-note-submit">
              {saving ? <Loader2 className="animate-spin mr-1" size={12} /> : <Send size={12} className="mr-1" />}
              Add note
            </Button>
          </div>
        </div>

        {/* Unified feed */}
        {loading ? (
          <div className="text-slate-500 text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading activity…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4" data-testid="timeline-empty">
            No activity yet.
          </p>
        ) : (
          <ol className="relative border-l border-slate-200 pl-4 space-y-3" data-testid="unified-timeline-list">
            {items.map((item, idx) => {
              const d = describe(item);
              const Icon = d.icon;
              return (
                <li key={`${item.kind}-${item.id || idx}`} className="relative" data-testid={`timeline-item-${item.kind}`}>
                  <span className={`absolute -left-[22px] w-6 h-6 rounded-full flex items-center justify-center ${d.tone} ring-2 ring-white`}>
                    <Icon size={12} />
                  </span>
                  <div className="text-xs text-slate-500">
                    {fmtDate(item.at)}{d.actor ? ` · ${d.actor}` : ''}
                  </div>
                  <div className="text-sm text-slate-800 font-medium flex items-center gap-2">
                    {d.title}
                    {d.detail && <span className="text-xs font-normal text-slate-500 capitalize">· {d.detail}</span>}
                  </div>
                  {d.body && (
                    <p className={`text-sm rounded px-2 py-1 mt-1 whitespace-pre-wrap ${item.kind === 'sms' ? 'bg-sky-50 text-slate-700' : 'bg-slate-50 text-slate-700'}`}>
                      {item.kind === 'sms' && <MessageSquare size={11} className="inline mr-1 text-sky-500 align-[-1px]" />}
                      {d.body}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
